// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 093 + 096 — the process landscape. The cross-process picture the
// glossary already records: one card per live process, one line per event
// that crosses from the process raising it to the process reacting to it.
// A card carries its domain and importance, its state and rank, and how
// its connections read: not assessed, n open questions, or reviewed.
// Lines and open questions open a dialog and send nothing. Filters live in
// the URL (the host owns them); the map is a scrolling region with zoom
// buttons; a card's connections light on request, never on hover alone.
// Same idiom as the step layer: SVG lines under absolutely positioned HTML
// cards.

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type UIEvent } from 'react';
import ShellDialog from './ShellDialog';
import {
  buildLandscape,
  connectionStatus,
  edgeLabelPoint,
  edgePath,
  highlightFor,
  humaniseEventName,
  landscapeRows,
  neighboursOf,
  openQuestions,
  stateLabel,
  visibleNodes,
  NODE_HEIGHT,
  NODE_WIDTH,
  UNPLACED_DOMAIN,
  UNPLACED_LABEL,
  type LandscapeDomain,
  type LandscapeGlossary,
  type LandscapeNode,
  type LandscapeProcess,
  type LensEntry,
} from '../utils/processLandscape';
import '../styles/ProcessLandscape.css';

export interface LandscapeFilters {
  query: string;
  domain: string;
  // A glossary entry as `${kind}:${name}`, or ''.
  who: string;
  openOnly: boolean;
  systems: boolean;
  mode: 'map' | 'table';
}

export const EMPTY_FILTERS: LandscapeFilters = { query: '', domain: '', who: '', openOnly: false, systems: false, mode: 'map' };

export interface LandscapeView {
  zoom: number;
  left: number;
  top: number;
}

export interface ProcessLandscapeProps {
  processes: LandscapeProcess[];
  glossary: LandscapeGlossary | null | undefined;
  domains?: LandscapeDomain[];
  focusedProcessId: string | null;
  onOpen: (processId: string) => void;
  // Controlled when both are given; otherwise the component keeps its own.
  filters?: Partial<LandscapeFilters>;
  onFiltersChange?: (next: LandscapeFilters) => void;
  // The map's zoom and scroll to restore, and where to report them.
  view?: LandscapeView | null;
  onViewChange?: (view: LandscapeView) => void;
}

const LENS_TITLE: Record<LensEntry['kind'], string> = {
  context: 'Bounded contexts',
  actor: 'People',
  system: 'Systems',
};

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
const FIT_MARGIN = 24;

function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

function parseLens(who: string): { kind: LensEntry['kind']; name: string } | null {
  const at = who.indexOf(':');
  if (at <= 0) return null;
  const kind = who.slice(0, at);
  if (kind !== 'context' && kind !== 'actor' && kind !== 'system') return null;
  return { kind, name: who.slice(at + 1) };
}

export default function ProcessLandscape({
  processes, glossary, domains = [], focusedProcessId, onOpen, filters: filtersProp, onFiltersChange, view, onViewChange,
}: ProcessLandscapeProps) {
  const [ownFilters, setOwnFilters] = useState<LandscapeFilters>({ ...EMPTY_FILTERS, ...(filtersProp ?? {}) });
  const filters: LandscapeFilters = filtersProp && onFiltersChange ? { ...EMPTY_FILTERS, ...filtersProp } : ownFilters;
  const update = (patch: Partial<LandscapeFilters>) => {
    const next = { ...filters, ...patch };
    setOwnFilters(next);
    onFiltersChange?.(next);
  };
  const anyFilter = Boolean(filters.query || filters.domain || filters.who || filters.openOnly);

  const [highlight, setHighlight] = useState<string | null>(null);
  const [openEdge, setOpenEdge] = useState<string | null>(null);
  const [questionsFor, setQuestionsFor] = useState<string | null>(null);
  const [sort, setSort] = useState<'storm' | 'name-asc' | 'name-desc'>('storm');

  const landscape = useMemo(() => buildLandscape(processes, glossary, { externalSystems: filters.systems }), [processes, glossary, filters.systems]);
  const nodeById = useMemo(() => new Map(landscape.nodes.map((node) => [node.id, node])), [landscape]);
  const visible = useMemo(
    () => visibleNodes(landscape, { query: filters.query, looseOnly: filters.openOnly, domain: filters.domain }),
    [landscape, filters.query, filters.openOnly, filters.domain],
  );
  const lens = useMemo(() => parseLens(filters.who), [filters.who]);
  const lit = useMemo(() => {
    const byLens = highlightFor(landscape, lens);
    if (!highlight) return byLens;
    const near = neighboursOf(landscape, highlight);
    return new Set([...byLens].filter((id) => near.has(id)));
  }, [landscape, lens, highlight]);

  const domainName = useCallback((domainId: string | null | undefined) => (
    domains.find((domain) => domain.id === domainId)?.name ?? UNPLACED_LABEL
  ), [domains]);

  const lensGroups = useMemo(() => {
    const groups = new Map<LensEntry['kind'], LensEntry[]>();
    for (const entry of landscape.lens) groups.set(entry.kind, [...(groups.get(entry.kind) ?? []), entry]);
    return (['context', 'actor', 'system'] as const).filter((kind) => groups.has(kind)).map((kind) => ({ kind, entries: groups.get(kind)! }));
  }, [landscape.lens]);

  const processNodes = landscape.nodes.filter((node) => node.kind === 'process');
  const shownProcesses = processNodes.filter((node) => visible.has(node.id));
  const openCount = shownProcesses.reduce((sum, node) => sum + node.raisedUnconsumed.length + node.incomingUnraised.length, 0);
  const notAssessedCount = shownProcesses.filter((node) => connectionStatus(node) === 'not-assessed').length;

  // ── The viewport: a scrolling region, zoomed by the buttons or Ctrl and
  // the wheel, panned by scrolling, the arrow keys, touch or a drag. ──
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(() => clampZoom(view?.zoom ?? 1));
  const pendingScroll = useRef<{ left: number; top: number } | null>(view ? { left: view.left, top: view.top } : null);
  const fitted = useRef(Boolean(view));

  const fitAll = useCallback(() => {
    const element = viewportRef.current;
    if (!element || !element.clientWidth || !element.clientHeight) return;
    const next = clampZoom(Math.min(1, (element.clientWidth - FIT_MARGIN) / landscape.width, (element.clientHeight - FIT_MARGIN) / landscape.height));
    setZoom(next);
    pendingScroll.current = { left: 0, top: 0 };
  }, [landscape.width, landscape.height]);

  useEffect(() => {
    if (fitted.current) return;
    fitted.current = true;
    fitAll();
  }, [fitAll]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || !pendingScroll.current) return;
    element.scrollLeft = pendingScroll.current.left;
    element.scrollTop = pendingScroll.current.top;
    pendingScroll.current = null;
  }, [zoom]);

  const report = useCallback((nextZoom: number) => {
    const element = viewportRef.current;
    onViewChange?.({ zoom: nextZoom, left: element?.scrollLeft ?? 0, top: element?.scrollTop ?? 0 });
  }, [onViewChange]);

  const changeZoom = (next: number) => {
    const clamped = clampZoom(next);
    setZoom(clamped);
    report(clamped);
  };

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setZoom((current) => {
        const next = clampZoom(current * (event.deltaY < 0 ? 1.1 : 0.9));
        onViewChange?.({ zoom: next, left: element.scrollLeft, top: element.scrollTop });
        return next;
      });
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [onViewChange, filters.mode]);

  const drag = useRef<{ startX: number; startY: number; left: number; top: number } | null>(null);
  const onBackgroundMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input, select, .landscape-node')) return;
    const element = viewportRef.current;
    if (!element) return;
    drag.current = { startX: event.clientX, startY: event.clientY, left: element.scrollLeft, top: element.scrollTop };
  };
  const onBackgroundMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const element = viewportRef.current;
    if (!drag.current || !element) return;
    element.scrollLeft = drag.current.left - (event.clientX - drag.current.startX);
    element.scrollTop = drag.current.top - (event.clientY - drag.current.startY);
  };
  const endDrag = () => { drag.current = null; };
  const onScroll = (_event: UIEvent<HTMLDivElement>) => { report(zoom); };

  const edgeOpen = openEdge ? landscape.edges.find((edge) => edge.id === openEdge) ?? null : null;
  const questionsNode = questionsFor ? nodeById.get(questionsFor) ?? null : null;

  const rows = useMemo(() => {
    const all = landscapeRows(landscape).filter((row) => visible.has(row.processId));
    if (sort === 'storm') return all;
    const direction = sort === 'name-asc' ? 1 : -1;
    return all.slice().sort((a, b) => direction * a.name.localeCompare(b.name));
  }, [landscape, visible, sort]);

  const connectionsCell = (node: Pick<LandscapeNode, 'id' | 'name' | 'state' | 'raisedUnconsumed' | 'incomingUnraised'>) => {
    const status = connectionStatus(node);
    if (status === 'not-assessed') return <span className="landscape-status landscape-status--none">Connections not explored</span>;
    if (status === 'reviewed') return <span className="landscape-status landscape-status--reviewed">No unmatched connections found</span>;
    const count = node.raisedUnconsumed.length + node.incomingUnraised.length;
    return (
      <button
        type="button"
        className="landscape-status landscape-status--open"
        aria-label={`${count} connection question${count === 1 ? '' : 's'} on ${node.name}`}
        onClick={() => setQuestionsFor(node.id)}
      >
        {count} connection question{count === 1 ? '' : 's'}
      </button>
    );
  };

  return (
    <div className="landscape" aria-label="Process landscape">
      <div className="landscape-toolbar">
        <input
          className="landscape-filter"
          type="search"
          aria-label="Find on landscape"
          placeholder="Find on landscape"
          value={filters.query}
          onChange={(event) => update({ query: event.target.value })}
        />
        <select
          className="landscape-select"
          aria-label="Filter by business domain"
          value={filters.domain}
          onChange={(event) => update({ domain: event.target.value })}
        >
          <option value="">All domains</option>
          {domains.map((domain) => <option key={domain.id} value={domain.id}>{domain.name}</option>)}
          <option value={UNPLACED_DOMAIN}>{UNPLACED_LABEL}</option>
        </select>
        <select
          className="landscape-select"
          aria-label="Filter by person or system"
          value={filters.who}
          onChange={(event) => update({ who: event.target.value })}
        >
          <option value="">People and systems</option>
          {lensGroups.map((group) => (
            <optgroup key={group.kind} label={LENS_TITLE[group.kind]}>
              {group.entries.map((entry) => (
                <option key={`${entry.kind}:${entry.name}`} value={`${entry.kind}:${entry.name}`}>
                  {humaniseEventName(entry.name)} ({entry.processIds.length})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <span className="landscape-spacer" />
        <div className="landscape-segment" role="group" aria-label="View">
          <button type="button" className={filters.mode === 'map' ? 'is-active' : ''} aria-pressed={filters.mode === 'map'} onClick={() => update({ mode: 'map' })}>Map</button>
          <button type="button" className={filters.mode === 'table' ? 'is-active' : ''} aria-pressed={filters.mode === 'table'} onClick={() => update({ mode: 'table' })}>Table</button>
        </div>
      </div>
      <div className="landscape-filterrow">
        <span className="landscape-summary">
          <strong>{shownProcesses.length}</strong> of {processNodes.length} process{processNodes.length === 1 ? '' : 'es'}
          {' · '}<strong>{openCount}</strong> connection question{openCount === 1 ? '' : 's'}
          {' · '}<strong>{notAssessedCount}</strong> connections not explored
        </span>
        <label className="landscape-toggle">
          <input type="checkbox" checked={filters.openOnly} onChange={(event) => update({ openOnly: event.target.checked })} />
          Open questions only
        </label>
        <label className="landscape-toggle">
          <input type="checkbox" checked={filters.systems} onChange={(event) => update({ systems: event.target.checked })} />
          External systems
        </label>
        {anyFilter ? (
          <button type="button" className="landscape-ghost" onClick={() => update({ query: '', domain: '', who: '', openOnly: false })}>Clear filters</button>
        ) : null}
      </div>

      {processNodes.length === 0 ? (
        <p className="landscape-empty">No processes yet. Narrate the first one and it appears here; the map grows as processes start to share events.</p>
      ) : shownProcesses.length === 0 ? (
        <div className="landscape-empty">
          <p>No processes match these filters.</p>
          <button type="button" className="landscape-ghost" onClick={() => update({ query: '', domain: '', who: '', openOnly: false })}>Clear filters</button>
        </div>
      ) : filters.mode === 'table' ? (
        <div className="landscape-table-wrap">
          <table className="landscape-table">
            <thead>
              <tr>
                <th scope="col">
                  <button
                    type="button"
                    className="landscape-sort"
                    aria-label={`Sort by process name, ${sort === 'name-asc' ? 'descending next' : 'ascending next'}`}
                    onClick={() => setSort(sort === 'name-asc' ? 'name-desc' : 'name-asc')}
                  >
                    Process {sort === 'name-asc' ? '↑' : sort === 'name-desc' ? '↓' : '↕'}
                  </button>
                </th>
                <th scope="col">Domain</th>
                <th scope="col">Importance</th>
                <th scope="col">Progress</th>
                <th scope="col">Steps</th>
                <th scope="col">Connections</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const node = nodeById.get(row.processId)!;
                return (
                  <tr key={row.processId} className={row.processId === focusedProcessId ? 'is-focused' : undefined}>
                    <td><button type="button" className="landscape-link" onClick={() => onOpen(row.processId)}>{row.name}</button></td>
                    <td>{domainName(row.domainId)}</td>
                    <td>{row.importance ?? ''}</td>
                    <td><span className={`landscape-state landscape-state--${row.state}`}>{stateLabel(row.state)}</span></td>
                    <td>{row.stepCount}</td>
                    <td>{connectionsCell(node)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="landscape-mapwrap">
          <div
            ref={viewportRef}
            className="landscape-viewport"
            tabIndex={0}
            aria-label="Process map. Scroll or use the arrow keys to move; use the zoom buttons to resize."
            onMouseDown={onBackgroundMouseDown}
            onMouseMove={onBackgroundMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onScroll={onScroll}
          >
            <div className="landscape-scroller" style={{ width: landscape.width * zoom, height: landscape.height * zoom }}>
              <div className="landscape-stage" style={{ width: landscape.width, height: landscape.height, transform: `scale(${zoom})` }}>
                <svg className="landscape-links" width={landscape.width} height={landscape.height} aria-hidden="true">
                  <defs>
                    <marker id="landscape-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                      <path d="M 0 0 L 10 5 L 0 10 z" className="landscape-arrow" />
                    </marker>
                  </defs>
                  {landscape.edges.map((edge) => {
                    const from = nodeById.get(edge.from);
                    const to = nodeById.get(edge.to);
                    if (!from || !to || !visible.has(from.id) || !visible.has(to.id)) return null;
                    const dim = !lit.has(from.id) || !lit.has(to.id);
                    return (
                      <path
                        key={edge.id}
                        d={edgePath(from, to)}
                        className={`landscape-edge${edge.from.startsWith('system:') ? ' landscape-edge--system' : ''}${dim ? ' is-dim' : ''}${openEdge === edge.id ? ' is-open' : ''}`}
                        markerEnd="url(#landscape-arrow)"
                      />
                    );
                  })}
                </svg>

                {landscape.edges.map((edge) => {
                  if (edge.events.length === 0) return null;
                  const from = nodeById.get(edge.from);
                  const to = nodeById.get(edge.to);
                  if (!from || !to || !visible.has(from.id) || !visible.has(to.id)) return null;
                  const point = edgeLabelPoint(from, to);
                  const dim = !lit.has(from.id) || !lit.has(to.id);
                  const text = edge.events.length === 1 ? humaniseEventName(edge.events[0]) : `${edge.events.length} events`;
                  return (
                    <button
                      key={`label:${edge.id}`}
                      type="button"
                      className={`landscape-edge-label${dim ? ' is-dim' : ''}${openEdge === edge.id ? ' is-open' : ''}`}
                      style={{ left: point.x, top: point.y }}
                      title={edge.events.map(humaniseEventName).join(', ')}
                      aria-label={`Inspect ${edge.events.map(humaniseEventName).join(', ')} from ${from.name} to ${to.name}`}
                      onClick={() => setOpenEdge(edge.id)}
                    >
                      {text}
                    </button>
                  );
                })}

                {landscape.nodes.map((node) => {
                  if (!visible.has(node.id)) return null;
                  const dim = !lit.has(node.id);
                  const focused = node.id === focusedProcessId;
                  const highlighted = node.id === highlight;
                  return (
                    <article
                      key={node.id}
                      className={`landscape-node landscape-node--${node.kind} landscape-node--${node.state}${dim ? ' is-dim' : ''}${focused ? ' is-focused' : ''}${highlighted ? ' is-highlighted' : ''}${node.importance === 'core' ? ' landscape-node--core' : ''}`}
                      style={{ left: node.x, top: node.y, width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
                      aria-current={focused ? 'true' : undefined}
                    >
                      {node.kind === 'process' ? (
                        <>
                          <div className="landscape-node-top">
                            <span className="landscape-node-domain">{domainName(node.domainId)}</span>
                            {node.importance ? (
                              <span className={`landscape-importance landscape-importance--${node.importance}`}>{node.importance}</span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="landscape-node-name"
                            aria-label={`Open ${node.name}`}
                            title="Open this process"
                            onClick={() => onOpen(node.id)}
                            onFocus={(event) => { event.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }}
                          >
                            {node.name} <span className="landscape-node-arrow" aria-hidden="true">↗</span>
                          </button>
                          <div className="landscape-node-meta">
                            <span className={`landscape-state landscape-state--${node.state}`}>{stateLabel(node.state)}</span>
                            {node.rank != null ? <span className="landscape-rank">{`#${node.rank}`}</span> : null}
                          </div>
                          <div className="landscape-node-foot">
                            <span className="landscape-node-steps">{node.stepCount} step{node.stepCount === 1 ? '' : 's'}</span>
                            {connectionsCell(node)}
                          </div>
                          <button
                            type="button"
                            className="landscape-connections"
                            aria-label={`Highlight connections for ${node.name}`}
                            aria-pressed={highlighted}
                            onClick={() => setHighlight(highlighted ? null : node.id)}
                          >
                            Connections
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="landscape-node-top">
                            <span className="landscape-node-domain">External system</span>
                          </div>
                          <span className="landscape-node-name landscape-node-name--system">{node.name}</span>
                        </>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="landscape-legend" aria-hidden="true">
            <span className="landscape-legend-line" /> Event flow · Select a label to inspect
          </div>
          <div className="landscape-maptools" role="group" aria-label="Map tools">
            <button type="button" aria-label="Zoom out" onClick={() => changeZoom(zoom - 0.1)}>−</button>
            <button type="button" aria-label="Reset zoom" onClick={() => changeZoom(1)}>{Math.round(zoom * 100)}%</button>
            <button type="button" aria-label="Zoom in" onClick={() => changeZoom(zoom + 0.1)}>+</button>
            <button type="button" onClick={() => { fitAll(); }}>Fit all</button>
            {highlight ? <button type="button" onClick={() => setHighlight(null)}>Clear highlight</button> : null}
          </div>
        </div>
      )}

      {edgeOpen ? (() => {
        const from = nodeById.get(edgeOpen.from)!;
        const to = nodeById.get(edgeOpen.to)!;
        return (
          <ShellDialog eyebrow="Shared events" title={`${from.name} → ${to.name}`} onClose={() => setOpenEdge(null)}>
            <ul>
              {edgeOpen.events.map((event) => <li key={event}>{humaniseEventName(event)}</li>)}
            </ul>
            <div className="shell-dialog-actions">
              {from.kind === 'process' ? (
                <button type="button" className="shell-dialog-btn" onClick={() => { setOpenEdge(null); onOpen(from.id); }}>Open source process</button>
              ) : null}
              <button type="button" className="shell-dialog-btn shell-dialog-btn--primary" onClick={() => { setOpenEdge(null); onOpen(to.id); }}>Open receiving process</button>
            </div>
          </ShellDialog>
        );
      })() : null}

      {questionsNode ? (
        <ShellDialog eyebrow={`Open questions · ${domainName(questionsNode.domainId)}`} title={questionsNode.name} onClose={() => setQuestionsFor(null)}>
          <ul>
            {openQuestions(questionsNode).map((entry) => <li key={`${entry.direction}:${entry.event}`}>{entry.question}</li>)}
          </ul>
          <p>Open the process to explore these questions. Nothing is sent to the facilitator.</p>
          <div className="shell-dialog-actions">
            <button type="button" className="shell-dialog-btn shell-dialog-btn--primary" onClick={() => { setQuestionsFor(null); onOpen(questionsNode.id); }}>
              Open process to investigate
            </button>
          </div>
        </ShellDialog>
      ) : null}
    </div>
  );
}
